// src/components/modals/ChangeProgramStatusModal.jsx
//
// Triggered from the program card's 3-dot menu ("שינוי סטטוס"). Lets the
// supplier pick between the three statuses they're allowed to set —
// DRAFT (default) / PENDING / APPROVED. APPROVED/ACTIVE/SUSPENDED set by
// GEFEN/admin and CLOSED (a separate, one-way action) aren't handled here.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../contexts/UserContext';
import SpinnerOverlay from '../ui/SpinnerOverlay';

const STATUS_CHOICES = [
  { value: 'DRAFT', label: 'טיוטה', hint: 'ברירת המחדל — התוכנית עדיין בהכנה' },
  { value: 'PENDING', label: 'ממתין לאישור', hint: 'נשלחה לבדיקה מול גפ״ן' },
  { value: 'APPROVED', label: 'מאושר', hint: 'התוכנית אושרה על ידי גפ״ן ומוכנה לפעילות' },
];

export default function ChangeProgramStatusModal({ isOpen, onClose, program, onSubmit }) {
  const { showAlert } = useUser();
  const [selected, setSelected] = useState(program?.status || 'DRAFT');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelected(program?.status || 'DRAFT');
  }, [isOpen, program]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !program) return null;

  const unchanged = selected === program.status;

  const handleSave = async () => {
    if (unchanged) return onClose?.();
    setSubmitting(true);
    try {
      await onSubmit?.(selected);
      onClose?.();
    } catch (err) {
      showAlert?.(err?.message || 'עדכון הסטטוס נכשל', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={onClose} />
      {submitting && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      <div
        className="relative z-[1] w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20 flex flex-col overflow-hidden"
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base leading-tight">שינוי סטטוס תוכנית</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">{program.name}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="px-5 sm:px-6 py-4 space-y-2">
          {STATUS_CHOICES.map((choice) => {
            const active = choice.value === selected;
            return (
              <button
                key={choice.value}
                type="button"
                onClick={() => setSelected(choice.value)}
                className={`w-full flex items-start gap-3 rounded-xl border px-3.5 py-3 text-right transition ${
                  active ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span
                  className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    active ? 'border-indigo-500' : 'border-gray-300'
                  }`}
                >
                  {active && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
                </span>
                <span className="min-w-0">
                  <span className={`block text-sm font-semibold ${active ? 'text-indigo-700' : 'text-gray-800'}`}>
                    {choice.label}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">{choice.hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm px-5 sm:px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-40"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || unchanged}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold shadow-md shadow-indigo-200 hover:shadow-lg hover:-translate-y-px transition-all duration-150 disabled:opacity-40 disabled:translate-y-0 disabled:shadow-none"
          >
            {submitting ? 'שומר…' : 'שמור סטטוס'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}